import { assertTurnTickContract } from './tick-contract.js';
import { ecId, ecLean, handBack, itemField, record, scalar, upper, type Row } from '../ability/context.js';
import { setRowAbilityCharges } from '../ability/charges/charge-entry.js';
import { orbQueueText } from '../ability/orbs/orb-queue.js';
import { statusItemId, statusRows } from '../statuses/expiry.js';
import { tickStatusProjection, upsertTickStatusProjection, type TurnTickResult } from './ticks.js';
import type { AbilityEntry } from './end-turn.js';
import saveShape from '../../../catalogs/janus/tick-save-shape.json';
import { applyStatusFrame, skillsWithoutStatuses, type EndTurnRowFrame } from './status-frame.js';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers.js';
import { encounterCreatureField, encounterField, posCoord } from '../../shared/array-types.js';
import { readItemField } from '../compile/item-field-reader.js';
import { applyTimedPassiveSpeedAddition } from '../compile/status-removal-runtime.js';
import { applyZoneRadiusChanges } from '../zones/end-of-turn-zones.js';
import { spawnDepartureIds } from '../spawn/row-plan.js';
import { projectShapeshiftFlag } from '../statuses/shapeshift-state.js';
import { applyStatusSkillLines, C1_CONTRACT } from '../compile/possessor-compile.js';
import { statusProjectionDependencies } from '../compile/status-attack-budget.js';
import { createStatusLayerReadContext } from '../compile/status-row-construction.js';
import { applyInventoryExpiry } from '../statuses/inventory-expiry.js';

/** Project the insert-only cast kind as its physical table without changing the saved plan. */
function projectCastInventory(tick: TurnTickResult): TurnTickResult {
  if (!tick.spawnRows?.writes.some(write => write.table === 'cast_inventory')) return tick;
  return { ...tick, spawnRows: { ...tick.spawnRows, writes: tick.spawnRows.writes.map(write =>
    write.table === 'cast_inventory' ? { ...write, table: 'inventory' as const } : write) } };
}

function deferredCreatureIds(tick: TurnTickResult): Set<string> {
  return new Set([...spawnDepartureIds(tick.spawnRows), ...Object.entries(tick.ec)
    .filter(([, columns]) => Number(columns.encounter_creature_dead ?? 0) === 1).map(([id]) => upper(id))]);
}

/** Physical row plans are installed after acceptance. Their caster projections can retire optimistically and roll back normally. */
export function provisionalTurnTick(tick: TurnTickResult, encounterId: string): TurnTickResult {
  const departures = deferredCreatureIds(tick);
  const sources = (tick.spawnRows?.writes ?? []).filter(w => w.table === 'statuses' && w.op === 'update'
    && w.row.status_deleted === 1 && w.before?.status_target_id).map(w => ({ status_id: String(w.row.status_id),
      status_item_id: String(w.before?.status_item_id ?? ''), target: String(w.before!.status_target_id) }));
  return { ...tick, spawnRows: undefined, ec: Object.fromEntries(Object.entries(tick.ec).filter(([id]) => !departures.has(upper(id)))),
    statusesRemoved: [...tick.statusesRemoved, ...sources] };
}

/** An acknowledgement must not replay charge/status writes over a later optimistic action. */
export function deferredTurnTick(tick: TurnTickResult): TurnTickResult {
  const departures = deferredCreatureIds(tick);
  return { rows: [], statusesAdded: [], statusesRemoved: [], statusesChanged: [], charges: [],
    ec: Object.fromEntries(Object.entries(tick.ec).filter(([id]) => departures.has(upper(id)))), spawnRows: tick.spawnRows };
}

/** Derive skill deltas before their status rows change; already decided defenses ride the native tick. */
export function turnTickStatusFrames(roster: Row[], tick: TurnTickResult, definition?: (id: string) => Row | null,
  primarySkills?: (actorId: string) => Record<string, Row> | undefined): Record<string, EndTurnRowFrame> {
  const frames: Record<string, EndTurnRowFrame> = {};
  for (const row of roster) {
    const removed = statusRows(row, 'target').filter(s => tick.statusesRemoved.some(r => r.status_id
      ? upper(r.status_id) === upper(s.status_id)
      : upper(r.status_item_id) === statusItemId(s) && upper(r.target) === upper(s.status_target_id))).map(s => upper(s.status_id));
    const supplied = tick.statusFrames?.[ecId(row)];
    const radiusChanged = statusRows(row, 'target').some(s => tick.radiusChanges?.some(c => upper(c.status_id) === upper(s.status_id)));
    const additions = definition ? tick.statusesAdded.filter(s => upper(s.target) === ecId(row)
      && !statusRows(row, 'target').some(held => upper(readItemField(held, 'status_id')) === upper(readItemField(s as unknown as Row, 'status_id')))) : [];
    const primary = additions.length ? primarySkills?.(ecId(row)) : undefined;
    const heldSkills = record(record(record(row.computed)?.creature)?.skills) ?? {};
    const before = primary ? { ...heldSkills } : heldSkills;
    for (const [key, patch] of Object.entries(primary ?? {})) {
      const entry = before[key] = { ...record(before[key]) };
      for (const [field, value] of Object.entries(patch)) { if (value === null) delete entry[field]; else entry[field] = value; }
    }
    const skills = removed.length ? skillsWithoutStatuses(primary ? { computed: { creature: { skills: before } } } : row, removed).skills : {};
    if (additions.length) {
      let projected: Row = { ...before };
      for (const [key, patch] of Object.entries(skills)) projected[key] = { ...record(projected[key]), ...patch };
      const read = createStatusLayerReadContext(statusProjectionDependencies(row, definition!));
      for (const status of additions) {
        const def = definition!(statusItemId(status as unknown as Row));
        if (!def) handBack('turn-tick:' + ecId(row) + ':status-definition-not-held');
        if (!['skill_roll_adv', 'skill_roll_dis'].some(key => Object.keys(read.layerEntries(def, key)).length)) continue;
        const next = applyStatusSkillLines(projected, def, true, upper(readItemField(status as unknown as Row, 'status_id')),
          C1_CONTRACT.skillEntries, read.layerEntries, () => true);
        if (typeof next === 'string') handBack('turn-tick:' + ecId(row) + ':status-skills:' + next);
        projected = next;
      }
      const addedIds = additions.map(status => upper(readItemField(status as unknown as Row, 'status_id'))).reverse();
      const heldIds = new Set(statusRows(row, 'target').map(status => upper(readItemField(status, 'status_id'))));
      for (const [key, entry] of Object.entries(projected)) {
        const next = record(entry) ?? {}, prior = record(before[key]) ?? {};
        if (next.adv_dis_stacks === prior.adv_dis_stacks && next.adv_dis_breakdown === prior.adv_dis_breakdown) continue;
        const lines = (record(next.adv_dis_breakdown)?.lines ?? []) as Row[];
        // Tick upserts prepend new rows. Cold skills walk those statuses last,
        // after globals and other sources; move only the added source lines.
        const added = addedIds.flatMap(id => lines.filter(line => !line.global_layer && upper(line.href_id) === id));
        const kept = lines.filter(line => !added.includes(line));
        const first = kept.findIndex(line => !line.global_layer && heldIds.has(upper(line.href_id)));
        const at = first < 0 ? kept.length : first;
        skills[key] = { adv_dis_stacks: Number(next.adv_dis_stacks ?? 0),
          adv_dis_breakdown: { lines: [...kept.slice(0, at), ...added, ...kept.slice(at)] } };
      }
    }
    if (removed.length || supplied || radiusChanged || Object.keys(skills).length) frames[ecId(row)] = { status_remove_ids: removed,
      skills: { ...skills, ...supplied?.skills },
      ...(supplied?.defenses !== undefined ? { defenses: supplied.defenses } : {}),
      ...(supplied?.attack_budget ? { attack_budget: supplied.attack_budget } : {}) };
  }
  return frames;
}

/** Apply the native turn tick without changing initiative, clocks or turn economy. */
export function applyTurnTick(roster: Row[], tick: TurnTickResult, definition?: (id: string) => Row | null, encounter?: Row): string[] {
  tick = projectCastInventory(tick);
  if (encounter) applyInventoryExpiry(encounter, roster, new Set([
    ...tick.statusesRemoved.flatMap(r => r.status_id ? [upper(r.status_id)] : []),
    ...(tick.spawnRows?.writes ?? []).filter(w => w.table === 'statuses' && w.op === 'update' && w.row.status_deleted === 1)
      .map(w => upper(w.row.status_id)),
  ]), definition);
  const touched = new Set<string>();
  applyZoneRadiusChanges(roster, tick.radiusChanges ?? []);
  for (const [id, frame] of Object.entries(turnTickStatusFrames(roster, tick))) {
    const row = roster.find(row => ecId(row) === id)!;
    applyStatusFrame(row, frame, definition); touched.add(id);
  }
  const member = (id: string): Row | undefined => roster.find(row => ecId(row) === upper(id));
  for (const [id, changes] of Object.entries(tick.ec)) {
    const row = member(id); if (!row) continue;
    Object.assign((row.encounter_creatures ??= {}) as Row, changes); touched.add(ecId(row));
    const vitality = record(record(row.computed)?.vitality);
    if (vitality) for (const key of ['v_current', 'v_temp'] as const) if (key in changes) {
      vitality[key] = changes[key];
      if (key === 'v_temp') vitality.base_v_temp = changes[key];
    }
  }
  const added = new Map<Row, string[]>();
  for (const status of tick.statusesAdded) {
    const entry = tickStatusProjection(status, upper((status as unknown as Row).status_id));
    const def = definition?.(statusItemId(entry)); if (def) entry.status_arr = def;
    for (const row of roster) if ([upper(status.target), upper(status.caster)].includes(ecId(row))) {
      if (ecId(row) === upper(status.target) && !statusRows(row, 'target').some(s => upper(readItemField(s, 'status_id')) === upper(readItemField(entry, 'status_id')))) {
        const ids = added.get(row) ?? []; ids.push(upper(readItemField(entry, 'status_id'))); added.set(row, ids);
      }
      upsertTickStatusProjection(row, entry); touched.add(ecId(row));
    }
  }
  for (const row of roster) {
    const statuses = record(row.statuses); if (!statuses) continue;
    for (const mode of ['target', 'caster'] as const) {
      const prior = statusRows(row, mode);
      statuses[mode] = prior.filter(s => !tick.statusesRemoved.some(r => r.status_id
        ? upper(r.status_id) === upper(s.status_id)
        : upper(r.status_item_id) === statusItemId(s) && upper(r.target) === upper(s.status_target_id)));
      if ((statuses[mode] as Row[]).length !== prior.length) touched.add(ecId(row));
      for (const s of statuses[mode] as Row[]) {
        const changed = tick.statusesChanged.find(c => upper(c.status_id) === upper(s.status_id));
        if (changed) { touched.add(ecId(row)); s.status_stack = changed.status_stack; }
      }
    }
  }
  for (const [row, ids] of added) applyTimedPassiveSpeedAddition(row, ids,
    id => definition?.(id) ?? record(record(row.catalog)?.[id]) ?? null);
  for (const id of touched) {
    const row = member(id);
    if (row && (tick.statusesAdded.some(status => upper(status.target) === id
      && itemField(definition?.(statusItemId(status as unknown as Row))
        ?? record(record(row.catalog)?.[statusItemId(status as unknown as Row)]), 'item_type') === 'creature')
      || itemField(row, 'shapeshifted') && tick.statusesRemoved.some(status => upper(status.target) === id))) {
      const reason = projectShapeshiftFlag(row, definition);
      // Missing form inputs use the existing exact-member recovery. A boundary
      // still installs its saved rows instead of stopping after a partial tick.
      if (reason) Object.defineProperty(row, '_action_computed_unavailable', { value: true, configurable: true, writable: true });
    }
  }
  for (const change of tick.charges) {
    const row = member(change.holder); if (row && setRowAbilityCharges(row, change.item_id, change.charges)) touched.add(ecId(row));
  }
  // Reconcile the shared death plan before another round reads equipment or floor stacks.
  const holders = [...roster, ...(encounter ? [encounter] : [])];
  for (const write of tick.spawnRows?.writes ?? []) {
    if (['inventory', 'corpse_inventory', 'death_loot'].includes(write.table)) {
      const saved = { inventory: write.row };
      const id = upper(readInventoryField(saved, 'inventory_id'));
      let row: Row | undefined;
      for (const holder of holders) {
        const key = holder === encounter ? 'encounter_inventories_arr' : 'inventory';
        const list = readInventoryRows(holder, key);
        const found = list.find(r => upper(readInventoryField(r, 'inventory_id')) === id);
        row ??= found;
        if (found && holder !== encounter) touched.add(ecId(holder));
        holder[key] = list.filter(r => upper(readInventoryField(r, 'inventory_id')) !== id);
      }
      row ??= { ...(definition?.(String(readInventoryField(saved, 'inventory_item_id'))) ?? {}), inventory: {} };
      Object.assign((row.inventory ??= {}) as Row, write.row);
      for (const key of ['x_pos', 'y_pos', 'z_pos']) if (key in write.row) ((row.position ??= {}) as Row)[key] = readInventoryField(saved, key);
      if (Number(readInventoryField(row, 'inventory_deleted') ?? 0)) continue;
      const owner = upper(readInventoryField(row, 'possess_id'));
      const holder = roster.find(r => ecId(r) === owner) ?? (encounter && upper(encounterField(encounter, 'encounter_id')) === owner ? encounter : undefined);
      if (holder) {
        const key = holder === encounter ? 'encounter_inventories_arr' : 'inventory';
        holder[key] = [...readInventoryRows(holder, key), row];
        if (holder !== encounter) touched.add(ecId(holder));
      }
    } else if (write.table === 'encounter_creatures') {
      const row = member(String(readItemField(write.row, 'encounter_creature_id')));
      if (row) { Object.assign((row.encounter_creatures ??= {}) as Row, write.row); touched.add(ecId(row)); }
    } else if (write.table === 'statuses' && write.op === 'update' && write.row.status_deleted === 1) {
      for (const row of [...roster, ...(encounter ? [encounter] : [])]) for (const mode of ['target', 'caster'] as const) {
        const statuses = record(row.statuses);
        if (statuses) {
          const before = statusRows(row, mode);
          statuses[mode] = before.filter(s => upper(s.status_id) !== upper(write.row.status_id));
          if ((statuses[mode] as Row[]).length !== before.length) touched.add(ecId(row));
        }
      }
    }
  }
  return [...touched];
}

/** Read original values through the same lean scalar and ability readers used by turns. */
export function prepareTurnTick(roster: Row[], tick: TurnTickResult, entries: (row: Row) => AbilityEntry[]): {
  before: Record<string, Record<string, number | string | null>>; charges: TurnTickResult['charges'];
} {
  tick = projectCastInventory(tick);
  assertTurnTickContract(tick);
  const member = (id: string): Row | undefined => roster.find(row => ecId(row) === upper(id));
  const before: Record<string, Record<string, number | string | null>> = {};
  const held = new Set(roster.map(ecId));
  const inventories = new Set((tick.spawnRows?.writes ?? []).filter(w => ['inventory', 'corpse_inventory', 'death_loot'].includes(w.table))
    .map(w => upper(readInventoryField({ inventory: w.row }, 'inventory_id'))));
  const participant = (id: string): void => { if (!held.has(upper(id))) handBack('turn-tick:participant-not-held'); };
  for (const row of tick.rows) for (const id of [row.roll_by, row.roll_to]) if (id && !inventories.has(upper(id))) participant(id);
  for (const status of tick.statusesAdded) { participant(status.target); participant(status.caster); }
  for (const status of tick.statusesRemoved) participant(status.target); // a stored caster may have departed
  for (const id of Object.keys(tick.statusFrames ?? {})) participant(id);
  for (const [id, changes] of Object.entries(tick.ec)) {
    const row = member(id); if (!row) handBack('turn-tick:before-member-not-held');
    const values: Record<string, number | string | null> = {};
    for (const key of Object.keys(changes)) {
      if (!(key in saveShape.ec)) handBack(`turn-tick:ec-column-unsupported:${key}`);
      const type = saveShape.ec[key as keyof typeof saveShape.ec];
      if (type === 'uuid') {
        values[key] = String(encounterCreatureField(record(row.encounter_creatures), key) ?? ''); continue;
      }
      if (key === 'orb_queue') { values[key] = orbQueueText(ecLean(row, key)); continue; }
      const raw = key === 'x_pos' || key === 'y_pos' || key === 'z_pos' ? posCoord(row, key) ?? 0 : scalar(ecLean(row, key));
      if (raw == null || !Number.isFinite(Number(raw))) handBack(`turn-tick:before-column-not-held:${key}`);
      values[key] = Number(raw);
    }
    before[id] = values;
  }
  const charges = tick.charges.map(c => {
    const holder = member(c.holder), entry = holder && entries(holder).find(e => upper(e.id) === upper(c.item_id));
    if (!entry || entry.charges === null) handBack('turn-tick:before-charge-not-held');
    return { ...c, before: entry.charges };
  });
  return { before, charges };
}
